import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd, UrlTree } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavController } from '@ionic/angular';
import { NavigationOptions } from '@ionic/angular/common/providers/nav-controller';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private navigationHistory: string[] = [];
  private ignoredPaths: string[] = [];

  constructor(
    private location: Location,
    private router: Router,
    private navCtrl: NavController,
    private logger: LoggerService
  ) {
    // Subscribe to router events to track navigation history
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (!this.shouldIgnorePath(event.urlAfterRedirects)) {
        this.navigationHistory.push(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Add a path to be ignored in navigation history
   * @param path Path to ignore (can be partial path)
   */
  addIgnoredPath(path: string): void {
    if (!this.ignoredPaths.includes(path)) {
      this.ignoredPaths.push(path);
    }
  }

  /**
   * Remove a path from ignored paths
   * @param path Path to remove from ignored list
   */
  removeIgnoredPath(path: string): void {
    this.ignoredPaths = this.ignoredPaths.filter(p => p !== path);
  }

  /**
   * Check if a path should be ignored
   * @param path Path to check
   * @returns boolean indicating if path should be ignored
   */
  private shouldIgnorePath(path: string): boolean {
    return this.ignoredPaths.some(ignoredPath => path.includes(ignoredPath));
  }

  /**
   * Navigate back with custom logic
   * @param fallbackPath Path to navigate to if no history exists
   * @param options Navigation options for animation and other settings
   */
  goBack(fallbackPath: string = '/items', options: NavigationOptions = { animated: false }): void {
    if (this.navigationHistory.length > 1) {
      // Remove current path from history
      this.navigationHistory.pop();
      // Get the previous path
      const previousPath = this.navigationHistory[this.navigationHistory.length - 1];
      this.logger.debug('previousPath', previousPath);
      if (previousPath) {
        this.navigationHistory.pop();
        this.navCtrl.navigateBack(previousPath, options);
      } else {
        this.navCtrl.navigateBack(fallbackPath, options);
      }
    } else {
      this.logger.debug('No previous path, using fallback', { fallbackPath });
      this.navCtrl.navigateRoot(fallbackPath);
    }
  }

  /**
   * Navigate to a specific path
   * @param path Path to navigate to
   * @param shouldIgnore Whether to ignore this navigation in history
   * @param options Navigation options for animation and other settings
   */
  navigateTo(path: string, shouldIgnore: boolean = false, options: NavigationOptions= { animated: true }): void {
    if (shouldIgnore) {
      this.addIgnoredPath(path);
    }
    this.navCtrl.navigateRoot(path, options);
  } 
  navigateForward(path: string, shouldIgnore: boolean = false, options: NavigationOptions= { animated: true }): void {
    if (shouldIgnore) {
      this.addIgnoredPath(path);
    }
    this.navCtrl.navigateForward(path, options);
  } 
  
  
  navigateBackward(path: string, shouldIgnore: boolean = false, options: NavigationOptions= { animated: true }): void {
    if (shouldIgnore) {
      this.addIgnoredPath(path);
    }
    this.navCtrl.navigateBack(path, options);
  }

  /**
   * Navigate to root with options
   * @param path Path to navigate to
   * @param options Navigation options for animation and other settings
   */
  navigateRoot(path: string, options: NavigationOptions = { animated: true }): void {
    this.navCtrl.navigateRoot(path, options);
  }

  /**
   * Clear navigation history
   */
  clearHistory(): void {
    this.navigationHistory = [];
  }

  /**
   * Get current navigation history
   * @returns Array of navigation paths
   */
  getNavigationHistory(): string[] {
    return [...this.navigationHistory];
  }
} 